/* eslint-disable max-len */
import { SealOfBiliteracyFileListItemModel } from '../SealOfBiliteracyFileListItemModel';
import { assessmentModel } from '../SealOfBiliteracyFormViewModel';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import yup from './CustomValidation/yup-extended';

export const SealOfBiliteracyValidationSchema = yup.object().shape({
  studentIdentifier: yup
    .string()
    .required(VALIDATION_MESSAGE.REQUIRED)
    .mustBeNumber()
    .min(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9))
    .max(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9)),
  studentLastOrSurname: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
  studentFirstName: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
  studentMiddleName: yup.string(),
  englishAssessmentName: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
  languageAssessmentName: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
});

export const createSealOfBiliteracyValidationSchema = (
  englishAssessment?: assessmentModel,
  worldLanguageAssessment?: assessmentModel,
  requiresDocumentUpload?: boolean,
  studentFiles?: SealOfBiliteracyFileListItemModel[],
  newlyUploadedFiles?: SealOfBiliteracyFileListItemModel[],
  hasScoreEdits?: boolean,
  isEditMode?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): yup.ObjectSchema<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalFields: Record<string, any> = {};

  if (englishAssessment && englishAssessment.measures) {
    additionalFields.englishAssessmentMeasures = yup.array().of(
      yup.object().shape({
        measureName: yup.string(),
        measureValue: yup
          .string()
          .test(
            'required-validation',
            'This field is required',
            function (value) {
              const path = this.path;
              const index = parseInt(path.split('[')[1].split(']')[0]);
              const measure = englishAssessment.measures[index];

              if (
                measure &&
                measure.isRequired &&
                (!value || value.trim() === '')
              ) {
                return this.createError({
                  message: VALIDATION_MESSAGE.REQUIRED,
                });
              }

              return true;
            }
          )
          .test('dynamic-validation', 'Invalid value', function (value) {
            const path = this.path;
            const index = parseInt(path.split('[')[1].split(']')[0]);
            const measure = englishAssessment.measures[index];

            if (!measure || !value) return true;

            if (measure.measureType === 'NumericRange') {
              const num = parseFloat(value);
              if (isNaN(num)) {
                return this.createError({ message: 'Must be a valid number' });
              }

              const lowScore = parseFloat(measure.measureLowScore);
              const highScore = parseFloat(measure.measureHighScore);
              if (num < lowScore || num > highScore) {
                return this.createError({
                  message: `Must be between ${lowScore} and ${highScore}`,
                });
              }

              const precision = parseInt(measure.measureNumericPrecision) || 0;
              const decimalPlaces = (value.split('.')[1] || '').length;
              if (decimalPlaces > precision) {
                return this.createError({
                  message: `Must have at most ${precision} decimal places`,
                });
              }
            }

            return true;
          }),
      })
    );
  }

  if (worldLanguageAssessment) {
    additionalFields.languageAssessmentCode = yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test('valid-language', 'Invalid language selection', function (value) {
        if (!value) return true;

        const isValidLanguage = worldLanguageAssessment.allowedLanguages?.some(
          (lang) => lang.languageCode === value
        );

        if (!isValidLanguage) {
          return this.createError({
            message: 'Please select a valid language',
          });
        }

        return true;
      });
  }

  if (worldLanguageAssessment && worldLanguageAssessment.measures) {
    additionalFields.languageAssessmentMeasures = yup.array().of(
      yup.object().shape({
        measureName: yup.string(),
        measureValue: yup
          .string()
          .test(
            'required-validation',
            'This field is required',
            function (value) {
              const path = this.path;
              const index = parseInt(path.split('[')[1].split(']')[0]);
              const measure = worldLanguageAssessment.measures[index];

              if (
                measure &&
                measure.isRequired &&
                (!value || value.trim() === '')
              ) {
                return this.createError({
                  message: VALIDATION_MESSAGE.REQUIRED,
                });
              }

              return true;
            }
          )
          .test('dynamic-validation', 'Invalid value', function (value) {
            const path = this.path;
            const index = parseInt(path.split('[')[1].split(']')[0]);
            const measure = worldLanguageAssessment.measures[index];

            if (!measure || !value) return true;

            if (measure.measureType === 'NumericRange') {
              const num = parseFloat(value);
              if (isNaN(num)) {
                return this.createError({ message: 'Must be a valid number' });
              }

              const lowScore = parseFloat(measure.measureLowScore);
              const highScore = parseFloat(measure.measureHighScore);
              if (num < lowScore || num > highScore) {
                return this.createError({
                  message: `Must be between ${lowScore} and ${highScore}`,
                });
              }

              const precision = parseInt(measure.measureNumericPrecision) || 0;
              const decimalPlaces = (value.split('.')[1] || '').length;
              if (decimalPlaces > precision) {
                return this.createError({
                  message: `Must have at most ${precision} decimal places`,
                });
              }
            }

            return true;
          }),
      })
    );
  }

  if (requiresDocumentUpload) {
    additionalFields.documentUploadRequired = yup
      .boolean()
      .test(
        'document-upload-required',
        'Document upload is required when scores are edited or Portfolio assessment type is selected',
        function () {
          const filesToCheck = hasScoreEdits
            ? newlyUploadedFiles
            : studentFiles;
          const hasUploadedDocuments = filesToCheck && filesToCheck.length > 0;

          if (!hasUploadedDocuments) {
            let message: string;
            if (hasScoreEdits) {
              message = 'New documents must be uploaded after score changes';
            } else if (isEditMode) {
              message =
                'At least one document must be uploaded when scores are edited or Portfolio assessment type is selected';
            } else {
              message =
                'Document upload is required when Portfolio assessment type is selected';
            }
            return this.createError({
              message,
            });
          }
          return true;
        }
      );
  }

  if (isEditMode) {
    additionalFields.studentIdentifier = yup
      .string()
      .mustBeNumber()
      .min(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9))
      .max(9, VALIDATION_MESSAGE.EXACT_NUMBER_LENGTH(9));
    additionalFields.studentLastOrSurname = yup.string();
    additionalFields.studentFirstName = yup.string();
  } else {
    additionalFields.studentLastOrSurname = yup.string();
    additionalFields.studentFirstName = yup.string();
  }

  return SealOfBiliteracyValidationSchema.shape(additionalFields);
};
